import type { createAudioEngine } from './engine';
import { setAudioEngine } from './index';

/** Events that count as a user gesture for the browser's autoplay policy. */
export const GESTURES = ['pointerdown', 'keydown', 'touchend'] as const;

type EngineModule = { createAudioEngine: typeof createAudioEngine };

/**
 * Nothing audio exists before the first user gesture (spec §1). On that gesture the context is
 * created and resumed synchronously, inside the gesture, which Safari requires; the engine chunk
 * (bus graph, voices, sound bank) is then imported lazily and handed the context. Any failure
 * leaves the game silent. Returns a function that removes the listeners if they haven't fired.
 */
export function installAudioUnlock(load: () => Promise<EngineModule> = () => import('./engine')) {
  const Ctx =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return () => {};

  const off = () => {
    for (const type of GESTURES) window.removeEventListener(type, onGesture, true);
  };
  function onGesture() {
    off();
    let ctx: AudioContext;
    try {
      ctx = new Ctx({ latencyHint: 'interactive' });
      void ctx.resume().catch(() => {});
    } catch (err) {
      console.debug('audio: no AudioContext', err);
      return;
    }
    load()
      .then((m) => setAudioEngine(m.createAudioEngine(ctx)))
      .catch((err: unknown) => {
        console.debug('audio: engine failed to load', err);
        void ctx.close().catch(() => {});
      });
  }
  for (const type of GESTURES) window.addEventListener(type, onGesture, true);
  return off;
}
